import { useLayoutEffect, useRef, useState, type ComponentProps, type MouseEvent, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { cn } from "../lib/cn";
import { intlLocale } from "../locale";

// Dense editorial table: strong top rule, hairline rows, no zebra, no vertical lines, no card.

export type TableProps = ComponentProps<"table"> & {
  /** `compact`: 28px rows for long single-line lists people scan (reports, logs, releases); rows with a second line stay default. */
  density?: "default" | "compact";
};

/**
 * The table and the frame around it. While the table fits its column the frame does not scroll, so the header row stays
 * in view as the page scrolls; when it is wider (a phone), the frame scrolls sideways instead and the header row scrolls
 * with the page.
 */
export function Table({ className, density = "default", ...props }: TableProps) {
  const frame = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const [wide, setWide] = useState(false);
  useLayoutEffect(() => {
    const f = frame.current;
    const t = table.current;
    if (!f || !t) return;
    const measure = () => setWide(t.offsetWidth > f.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(f);
    observer.observe(t);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={frame} className={cn("w-full", wide && "overflow-x-auto")}>
      <table ref={table} data-density={density} className={cn("group/table w-full border-collapse text-sm", className)} {...props} />
    </div>
  );
}

/**
 * The header row, stuck to the top of the window while its table is on screen. Its strong rule is drawn by each cell, so
 * the rule travels with the row.
 */
export function THead({ className, ...props }: ComponentProps<"thead">) {
  return (
    <thead
      className={cn(
        "[&_tr]:hover:bg-transparent [&_th]:sticky [&_th]:top-0 [&_th]:z-[5] [&_th]:bg-surface",
        "[&_th]:after:pointer-events-none [&_th]:after:absolute [&_th]:after:inset-x-0 [&_th]:after:bottom-0 [&_th]:after:h-px [&_th]:after:bg-line-strong",
        className,
      )}
      {...props}
    />
  );
}

export function TBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={cn("[&_tr]:border-b [&_tr]:border-line [&_tr:last-child]:border-b-0", className)} {...props} />;
}

/** A click that should not open the row: on something interactive inside it, or the end of a text selection. */
function isInnerControl(event: MouseEvent<HTMLTableRowElement>) {
  const target = event.target as HTMLElement;
  if (target.closest("a, button, input, select, textarea, label, [role=button]")) return true;
  return (window.getSelection()?.toString() ?? "") !== "";
}

/**
 * A table row. `linked` makes the whole row open its record: a click anywhere follows the row's link — the first `<a>`
 * in it, or the one marked `data-row-link` — so the record's name stays the keyboard and screen-reader way in, and
 * Ctrl/⌘-click or a middle click open it in a new tab as they would on the link. A linked row is one `j` and `k` move
 * through (`useListKeys`): it takes the hover tint while its link has the focus, and stops clear of the stuck header.
 */
export function TR({ className, linked = false, onClick, onAuxClick, ...props }: ComponentProps<"tr"> & { linked?: boolean }) {
  const link = (row: HTMLTableRowElement) => row.querySelector<HTMLAnchorElement>("a[data-row-link]") ?? row.querySelector<HTMLAnchorElement>("a[href]");

  return (
    <tr
      data-linked={linked ? "" : undefined}
      className={cn(
        "transition-colors duration-150 ease-out-quick hover:bg-surface-2/60 data-[state=selected]:bg-selected",
        linked && "cursor-pointer scroll-mt-12 scroll-mb-4 focus-within:bg-surface-2/60",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        if (!linked || event.defaultPrevented || isInnerControl(event)) return;
        const anchor = link(event.currentTarget);
        if (!anchor) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) window.open(anchor.href, "_blank", "noopener");
        else anchor.click();
      }}
      onAuxClick={(event) => {
        onAuxClick?.(event);
        if (!linked || event.button !== 1 || isInnerControl(event)) return;
        const anchor = link(event.currentTarget);
        if (anchor) window.open(anchor.href, "_blank", "noopener");
      }}
      {...props}
    />
  );
}

/**
 * `from` hides a secondary column below a width, so a table on a phone keeps the few columns that identify a row and
 * folds the rest away (or under the name). Put the same `from` on the header cell and its body cells. `xl` (1280) is for
 * a wide table beside the sidebar, whose last columns would otherwise scroll at 1024.
 */
type ColumnFrom = { from?: "sm" | "md" | "lg" | "xl" };
const fromClass = { sm: "hidden sm:table-cell", md: "hidden md:table-cell", lg: "hidden lg:table-cell", xl: "hidden xl:table-cell" } as const;

export type SortDirection = "asc" | "desc";

/** A column the list can be put in order by: which way it is ordered now (null: by another column) and the click. */
export type ColumnSort = { direction: SortDirection | null; onSort: () => void };

/**
 * A sortable column's header from the list's `sort` as its URL keeps it ("placed", or "-placed" for the other way):
 * the direction it is ordered in now, and the `sort` a click asks for. A first click orders by the column `first` way
 * (newest or largest first for dates and figures, A to Z for names); a second turns it round.
 */
export function sortBy(current: string, column: string, first: SortDirection = "asc") {
  const active = current.replace(/^-/, "") === column;
  const direction: SortDirection | null = active ? (current.startsWith("-") ? "desc" : "asc") : null;
  const then = active ? (direction === "asc" ? "desc" : "asc") : first;
  return { direction, next: then === "desc" ? `-${column}` : column };
}

/**
 * A whole list (one that is not paged, such as a report's table) put in order on the page by its headers: the rows in
 * the picked order and each sortable header's `sort`. `initial` is the order the rows arrive in ("-value"); `columns`
 * read each sortable column's value from a row. A paged list is sorted by the server instead, through `sortBy`.
 */
export function useSortedRows<T>(rows: readonly T[], initial: string, columns: Record<string, (row: T) => string | number>) {
  const [sort, setSort] = useState(initial);
  const read = columns[sort.replace(/^-/, "")];
  const sign = sort.startsWith("-") ? -1 : 1;
  const sorted = read
    ? [...rows].sort((a, b) => {
        const x = read(a);
        const y = read(b);
        return sign * (typeof x === "string" ? x.localeCompare(String(y), intlLocale()) : x - (y as number));
      })
    : [...rows];
  const header = (column: string, first: SortDirection = "asc"): ColumnSort => {
    const s = sortBy(sort, column, first);
    return { direction: s.direction, onSort: () => setSort(s.next) };
  };
  return { rows: sorted, header };
}

/**
 * A header cell. With `sort` its words become the button that orders the list by the column, the arrow saying which
 * way it is ordered now; screen readers hear it through `aria-sort`. `bar` marks the header of a column whose cells draw
 * bars (`TDNum bar`): it ends over the figures, not over the bars.
 */
export function TH({ className, from, sort, bar, children, ...props }: ComponentProps<"th"> & ColumnFrom & { sort?: ColumnSort; bar?: boolean }) {
  return (
    <th
      aria-sort={sort ? (sort.direction === "asc" ? "ascending" : sort.direction === "desc" ? "descending" : "none") : undefined}
      className={cn(
        "h-9 px-3 text-start align-middle text-xs font-medium text-ink-2 group-data-[density=compact]/table:h-8",
        // The bar's track (w-24) and the gap before it (gap-3), beside the cell's own padding.
        bar && "sm:pe-30",
        from && fromClass[from],
        className,
      )}
      {...props}
    >
      {sort ? <SortButton sort={sort}>{children}</SortButton> : children}
    </th>
  );
}

function SortButton({ sort, children }: { sort: ColumnSort; children: ReactNode }) {
  const Arrow = sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={sort.onSort}
      className={cn(
        "inline-flex items-center gap-1 transition-colors duration-150 ease-out-quick hover:text-ink",
        sort.direction && "text-ink",
      )}
    >
      {children}
      {sort.direction && <Arrow aria-hidden className="size-3 shrink-0" />}
    </button>
  );
}

export function TD({ className, from, ...props }: ComponentProps<"td"> & ColumnFrom) {
  return <td className={cn("h-10 px-3 align-middle text-ink group-data-[density=compact]/table:h-8", from && fromClass[from], className)} {...props} />;
}

/**
 * Numeric cell: the sans with tabular figures, end-aligned, never wrapped (a figure broken over two lines reads as two).
 * Mono is for codes (order, invoice and reference numbers, SKUs), not for amounts, counts or dates.
 *
 * `bar` (0 to 1) draws the figure's size as a bar in the cell, for the one column of a report a person compares down
 * (a store's value, a week's perfect orders): a share as itself, a count against the column's largest. The figure comes
 * first and the bar after it, in a track of the same length on every row at the cell's end, so the bars share a baseline
 * and each touches its own figure (a bar at the cell's start would sit against the column before). Hidden below `sm`,
 * where the column has no room for it. The column's total takes `bar={null}`: the track's room without a bar, so the
 * total stands under the figures.
 */
export function TDNum({ className, from, bar, children, ...props }: ComponentProps<"td"> & ColumnFrom & { bar?: number | null }) {
  const share = bar === undefined ? null : bar === null || !Number.isFinite(bar) ? 0 : Math.min(1, Math.max(0, bar));
  return (
    <td
      className={cn(
        "h-10 whitespace-nowrap px-3 text-end align-middle tabular-nums text-ink group-data-[density=compact]/table:h-8",
        from && fromClass[from],
        className,
      )}
      {...props}
    >
      {share === null ? (
        children
      ) : (
        <span className="flex items-center justify-end gap-3">
          <span>{children}</span>
          <span aria-hidden className="hidden h-1.5 w-24 shrink-0 sm:block">
            <span className="block h-full bg-ink-4" style={{ width: `${share * 100}%` }} />
          </span>
        </span>
      )}
    </td>
  );
}

/**
 * The totals under the rows, in the same columns, after one strong rule. Only for a whole list, or for figures the server
 * totals over every page: a sum of the page on screen would say less than it seems to. Several rows (before VAT, VAT,
 * with VAT) share the rule above the first.
 */
export function TFoot({ className, ...props }: ComponentProps<"tfoot">) {
  return <tfoot className={cn("[&_tr:first-child]:border-t [&_tr:first-child]:border-line-strong [&_tr]:hover:bg-transparent [&_td]:font-medium", className)} {...props} />;
}

/**
 * The row that opens beneath a selected row: a full-width slab on the canvas tone between two strong rules, holding the
 * facts and actions of the row above it, laid out side by side now that there is room. One open at a time; the row
 * above carries `data-state="selected"`. For anything longer than a few fields, give the record a page instead.
 */
export function ExpandedRow({ colSpan, className, children, ...props }: ComponentProps<"tr"> & { colSpan: number }) {
  return (
    <tr data-expanded="" className="hover:bg-transparent" {...props}>
      <td colSpan={colSpan} className="p-0">
        <div className={cn("border-t border-b border-line-strong bg-canvas px-4 py-5 md:px-6", className)}>{children}</div>
      </td>
    </tr>
  );
}
